(() => {
  let snapshot;
  let request;
  let fetchedAt = 0;
  const normalizeSku = (sku) => String(sku || '').trim().toUpperCase().replace(/-PRE$/, '').replace(/-2XL$/, '-XXL');
  function validate(data) {
    if (!data || data.schema_version !== 1 || !data.skus || !Number.isFinite(Date.parse(data.refreshed_at))) throw new Error('Invalid reservation data');
    return data;
  }
  function seed(element) {
    const initial = element.querySelector('[data-reservation-initial]');
    if (!initial) return;
    try {
      const data = validate(JSON.parse(initial.textContent));
      if (!snapshot || Date.parse(data.refreshed_at) > Date.parse(snapshot.refreshed_at)) {
        snapshot = data;
        fetchedAt = Date.now();
      }
    } catch { /* Use the last successful snapshot or the asset fallback. */ }
  }
  async function fetchSnapshot(element, fallback = false) {
    const url = new URL(fallback ? element.dataset.fallbackSource : element.dataset.source, location.origin);
    url.searchParams.delete('v');
    url.searchParams.set('refresh', Math.floor(Date.now() / 60000));
    if (!fallback) {
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { cache: 'no-store', credentials: fallback ? 'omit' : 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Reservation data unavailable');
      if (fallback) return validate(await response.json());
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = html.querySelector('[data-lbs-reservation-snapshot]');
      if (!payload) throw new Error('Reservation snapshot missing');
      return validate(JSON.parse(payload.textContent));
    } finally { clearTimeout(timer); }
  }
  async function getSnapshot(element, force = false) {
    // Draft-only Oct 21 data: never fall back to the shop-wide older shipment.
    if (!force && snapshot && Date.now() - fetchedAt < 60000) return snapshot;
    if (request) return request;
    request = (async () => {
      try { snapshot = await fetchSnapshot(element, true); fetchedAt = Date.now(); return snapshot; }
      catch (e) { if(snapshot) return snapshot; throw e; }
    })().finally(() => {request = null;});
    return request;
  }
  class SkuReservation extends HTMLElement {
    connectedCallback() { this.render(); }
    async render(force = false) {
      const sku = normalizeSku(this.dataset.sku);
      try {
        const data = await getSnapshot(this, force);
        if (!this.isConnected || normalizeSku(this.dataset.sku) !== sku) return;
        const row = data.skus[sku];
        if (!row || !Number.isInteger(row.incoming) || !Number.isInteger(row.reserved) || row.incoming <= 0 || row.reserved <= 0) {
          this.hidden = true;
          delete this.dataset.renderedSku;
          return;
        }
        const percent = Math.min(100, Math.floor(row.reserved / row.incoming * 100));
        if (percent <= 39) { this.hidden = true; delete this.dataset.renderedSku; return; }
        const full = row.reserved >= row.incoming;
        this.querySelector('[data-reservation-heading]').textContent = full ? this.dataset.fullHeading : this.dataset.headingTemplate.replace('__PERCENT__', percent);
        this.querySelector('[data-reservation-copy]').textContent = full ? this.dataset.fullCopy : this.dataset.reserveCopy;
        this.querySelector('[data-reservation-fill]').style.width = percent + '%';
        this.querySelector('[data-reservation-progress]').setAttribute('aria-valuenow', String(percent));
        this.querySelector('[data-reservation-reserved]').textContent = percent + '% reserved';
        this.querySelector('[data-reservation-available]').textContent = (100 - percent) + '% available';
        this.dataset.percent = String(percent);
        this.dataset.refreshedAt = data.refreshed_at;
        this.dataset.renderedSku = sku;
        this.hidden = false;
      } catch {
        if (this.dataset.renderedSku !== sku) this.hidden = true;
      }
    }
  }
  if (!customElements.get('lbs-sku-reservation')) customElements.define('lbs-sku-reservation', SkuReservation);
  const refresh = () => document.querySelectorAll('lbs-sku-reservation').forEach(element => element.render(true));
  window.setInterval(refresh, 15 * 60 * 1000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  const scarcitySourceSelector = 'product-price [ref="priceContainer"] lbs-sku-reservation';
  const scarcityMovedSelector = 'lbs-sku-reservation[data-lbs-below-purchase]';
  const watchedDetails = new WeakSet();

  function findScarcityAnchor(details) {
    const buyButtons = details.querySelector('.buy-buttons-block');
    if (!buyButtons) return null;
    const stack = buyButtons.parentElement;
    if (!stack) return buyButtons;
    let anchor = buyButtons;
    const controls = stack.querySelectorAll('button, a, [role="button"], input[type="button"], input[type="submit"]');
    for (const control of controls) {
      const label = control instanceof HTMLInputElement ? control.value : control.textContent;
      if (!/\bnotify\s+me\b/i.test(label || '')) continue;
      if (control.closest('[hidden], [aria-hidden="true"]')) continue;
      if (!control.getClientRects().length || getComputedStyle(control).visibility === 'hidden') continue;
      let directChild = control;
      while (directChild.parentElement && directChild.parentElement !== stack) directChild = directChild.parentElement;
      if (directChild.parentElement !== stack || directChild === buyButtons) continue;
      if (buyButtons.compareDocumentPosition(directChild) & Node.DOCUMENT_POSITION_FOLLOWING) anchor = directChild;
    }
    return anchor;
  }

  function placeScarcityBelowPurchase(details, expectedBadge) {
    if (!details?.isConnected) return;
    const anchor = findScarcityAnchor(details);
    if (!anchor) return;
    const inPrice = details.querySelector(scarcitySourceSelector);
    const moved = details.querySelector(scarcityMovedSelector);
    if (expectedBadge === null) {
      moved?.remove();
      inPrice?.remove();
      return;
    }
    const expectedSku = expectedBadge?.dataset.sku;
    let badge = inPrice;
    if (badge && expectedSku && badge.dataset.sku !== expectedSku) badge = null;
    if (!badge && moved && (!expectedSku || moved.dataset.sku === expectedSku)) badge = moved;
    if (!badge && expectedBadge) badge = expectedBadge.cloneNode(true);
    if (!badge) return;
    if (moved && moved !== badge) moved.remove();
    if (inPrice && inPrice !== badge && expectedSku) inPrice.remove();
    badge.setAttribute('data-lbs-below-purchase', '');
    if (anchor.nextElementSibling !== badge) anchor.insertAdjacentElement('afterend', badge);
  }

  function watchScarcityPosition(details) {
    if (watchedDetails.has(details)) return;
    watchedDetails.add(details);
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled || !details.isConnected) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        placeScarcityBelowPurchase(details);
      });
    });
    observer.observe(details, { childList: true, subtree: true });
  }

  function initializeScarcityPosition() {
    document.querySelectorAll('#MainContent .product-details').forEach((details) => {
      placeScarcityBelowPurchase(details);
      watchScarcityPosition(details);
    });
  }

  document.addEventListener('variant:update', (event) => {
    const details = event.target instanceof Element ? event.target.closest('#MainContent .product-details') : null;
    if (!details) return;
    const expectedBadge = event.detail?.data?.html?.querySelector(scarcitySourceSelector) || null;
    requestAnimationFrame(() => placeScarcityBelowPurchase(details, expectedBadge));
  });

  document.addEventListener('shopify:section:load', initializeScarcityPosition);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeScarcityPosition, { once: true });
  } else {
    initializeScarcityPosition();
  }

})();
